import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDown } from "lucide-react";
import heroLines from "@/assets/hero-lines-white.webp";

const HERO_IMG = "/images/hero-face-dim.jpg";
const HERO_IMG_CLOSED = "/images/hero-face-eyes-closed-dim.jpg";

const LINES = ["Дизайн стрижки", "по золотому", "сечению"];

const scrollToPreorder = () => {
  if (window.__lenis) {
    window.__lenis.scrollTo("#preorder", { offset: -20, duration: 1.6 });
  } else {
    document.querySelector("#preorder")?.scrollIntoView({ behavior: "smooth" });
  }
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative flex min-h-screen flex-col justify-end overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div style={{ y: imageY }} className="absolute inset-0">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Лицо с линиями золотого сечения и маской Марквардта"
            className="absolute inset-0 h-full w-full object-cover object-[50%_28%] md:object-center"
          />
          <img
            src={HERO_IMG_CLOSED}
            alt=""
            aria-hidden="true"
            className="hero-blink absolute inset-0 h-full w-full object-cover object-[50%_28%] md:object-center"
          />
          <img
            src={heroLines}
            alt=""
            aria-hidden="true"
            className="hero-lines-draw absolute inset-0 h-full w-full object-cover object-[50%_28%] md:object-center"
          />
          <img
            src={heroLines}
            alt=""
            aria-hidden="true"
            className="hero-lines-front absolute inset-0 h-full w-full object-cover object-[50%_28%] md:object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-[#0A0A0A]/10" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/90 via-[#0A0A0A]/30 to-transparent" />
      </motion.div>

      <motion.div
        style={{ opacity: contentOpacity }}
        className="relative z-10 px-6 pb-28 pt-40 md:px-12 lg:px-24"
      >
        <div className="overflow-hidden">
          <motion.p
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="mb-8 font-jetbrains text-xs uppercase tracking-[0.35em] text-[#C2A878]"
            data-testid="hero-eyebrow"
          >
            Маска Марквардта · Золотое сечение · Уже в Telegram
          </motion.p>
        </div>

        <h1 className="font-cormorant font-light leading-[0.92] tracking-tight text-[#F4F4F0]">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.3 + i * 0.16, ease: [0.16, 1, 0.3, 1] }}
                className={`block text-[clamp(3.2rem,11vw,9.5rem)] ${
                  i === 1 ? "italic text-[#C2A878]" : ""
                }`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 max-w-md text-base leading-relaxed text-[#A3A3A3] md:text-lg"
          data-testid="hero-subtitle"
        >
          Дизайн Про измеряет пропорции вашего лица по маске Марквардта и
          показывает, какая форма вам действительно подойдёт, — до того, как мастер возьмёт ножницы.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 flex flex-wrap items-center gap-6"
        >
          <Link
            to="/preview"
            className="border border-[#C2A878] px-10 py-4 font-jetbrains text-xs uppercase tracking-[0.25em] text-[#C2A878] transition-colors duration-500 hover:bg-[#C2A878] hover:text-[#0A0A0A]"
            data-testid="hero-test-app-button"
          >
            Открыть приложение
          </Link>
          <button
            onClick={scrollToPreorder}
            className="group border border-[#C2A878] px-10 py-4 font-jetbrains text-xs uppercase tracking-[0.25em] text-[#C2A878] transition-colors duration-500 hover:bg-[#C2A878] hover:text-[#0A0A0A]"
            data-testid="hero-cta-button"
          >
            Получить доступ
          </button>
          <span className="flex items-center gap-3 font-jetbrains text-xs uppercase tracking-[0.2em] text-[#525252]">
            Листайте вниз
            <ArrowDown size={14} strokeWidth={1.5} className="animate-bounce" />
          </span>
        </motion.div>
      </motion.div>
    </section>
  );
};
